import React, {
  memo,
  useRef,
  useState,
  forwardRef,
  useCallback,
  useImperativeHandle,
} from 'react';

import { locale } from 'src/config';
import TextInput from 'src/components/TextInput';
import { checkNumberTyping, toParsableNumber } from 'src/utils/standards';

const checkParam = { locale, decimals: 2 };

export interface NumberInputFieldRef {
  setError: (error: { error?: boolean; helperText?: string } | null) => void;
}

export interface NumberInputFieldProps {
  value?: string | number;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlurChange?: (
    event: React.FocusEvent<HTMLInputElement>,
    value: string | number,
  ) => void;
  decimals?: number;
  error?: boolean;
  helperText?: string;
  [key: string]: any; // eslint-disable-line
}

const NumberInputField = forwardRef<NumberInputFieldRef, NumberInputFieldProps>(
  (props, ref) => {
    const {
      value,
      onChange,
      onBlurChange,
      error: externalError,
      helperText: externalHelperText,
      ...otherProps
    } = props;

    const originalValue = useRef(value);
    const [error, setError] = useState<{
      error?: boolean;
      helperText?: string;
    } | null>(null);
    const [number, setNumber] = useState(value);

    useImperativeHandle(ref, () => ({ setError }));

    const handleNumberChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value: numberValue } = event.target;
        if (
          numberValue === '' ||
          numberValue == null ||
          checkNumberTyping(numberValue, checkParam)
        ) {
          setNumber(numberValue);
          onChange?.(event);
        }
      },
      [onChange],
    );

    const handleBlur = useCallback(
      async (event: React.FocusEvent<HTMLInputElement>) => {
        const originalNumber = originalValue.current;
        if (typeof onBlurChange === 'function' && number !== undefined) {
          const numberString = String(number);
          const parsedNumber = toParsableNumber(numberString, locale);
          const isNumberValid = !Number.isNaN(parseFloat(parsedNumber));
          if (
            (number && isNumberValid && number !== originalNumber) ||
            (originalNumber &&
              (number == null || number === '' || isNumberValid) &&
              originalNumber !== number)
          ) {
            onBlurChange(event, number);
            originalValue.current = number;
          }
        }
      },
      [number, onBlurChange],
    );

    return (
      <TextInput
        {...otherProps}
        variant="standard"
        onBlur={handleBlur}
        value={number || ''}
        onChange={handleNumberChange}
        error={externalError || error?.error}
        helperText={externalHelperText || error?.helperText}
      />
    );
  },
);

export default memo(NumberInputField);
